import SceneService from "../scene/scene_service";
import RenderService from "../render/render_service";
import ShaderService from "../shader/shader_service";
import ComputeService from "../compute/compute_service";
import CaptureService from "../capture/capture_service";
import InputService from "../input/inputservice";
import ResourceService from "../resource/resourceservice";
import CollisionService from "../collision/collision_service";
import BrowserInfo from "./browserinfo";

type GlobalStage = typeof globalThis & {
  __stage?: Stage;
};

export default class Stage {
  scene_service: SceneService;
  render_service: RenderService;
  shader_service: ShaderService;
  compute_service: ComputeService;
  capture_service: CaptureService;
  input_service:InputService;
  resource_service:ResourceService;
  collision_service:CollisionService;
  canvas: HTMLCanvasElement | null = null;

  device_iphone: boolean;
  device_mobile: boolean;
  footerstrings: string[];
  browser_info: BrowserInfo;

  webgpu_supported: boolean | null = null;
  webgpu_checked = false;

  private animationFrameId: number | null = null;
  private lastFrameTime: number | null = null;

  private constructor() {
     console.log('Stage constructor called');
    this.shader_service = new ShaderService();
    this.resource_service = new ResourceService();
    this.collision_service = new CollisionService();
    this.render_service = new RenderService(this.shader_service,this.resource_service,this.collision_service);
  
    this.compute_service = new ComputeService();
    this.capture_service = new CaptureService();
    this.input_service = new InputService();

    const device = this.shader_service.get_device();
    if(device)
    {
       this.resource_service.createContext(device);
    }

    this.scene_service = new SceneService();
 

    this.device_iphone = /iPhone/i.test(navigator.userAgent);
    this.footerstrings = ["echoplay.ai", "", "", ""];
    this.device_mobile = this.isMobileDevice();
    this.browser_info = new BrowserInfo();
  }

  start() 
  {
    if (this.animationFrameId !== null) return;
    this.lastFrameTime = null;
    this.animationFrameId = requestAnimationFrame(this.frame);
    if(this.render_service.canvas)
    {
        this.input_service.attach(this.render_service.canvas);
    }

  }

  stop()
   {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.lastFrameTime = null;
    this.input_service.detach();
  }

  async detect_webgpu_support(): Promise<boolean> {
    if (this.webgpu_checked && this.webgpu_supported !== null) {
      return this.webgpu_supported;
    }

    if (typeof navigator === 'undefined' || !navigator.gpu) {
      this.webgpu_supported = false;
      this.webgpu_checked = true;
      return false;
    }

    try {
      const adapter = await navigator.gpu.requestAdapter();
      this.webgpu_supported = !!adapter;
      this.webgpu_checked = true;
      return this.webgpu_supported;
    } catch {
      this.webgpu_supported = false;
      this.webgpu_checked = true;
      return false;
    }
  }

  async init_services() {
    const supported = await this.detect_webgpu_support();

    if (!supported) {
      return false;
    }

    await this.shader_service.init();
    await this.compute_service.init();
    await this.capture_service.init()
    return true;
  }

  isMobileDevice(): boolean {
    if (typeof navigator === 'undefined') return false;

    const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    return isMobileUA || hasTouch;
  }

  get_browser_info() {
    return this.browser_info.get_info(this.webgpu_supported ?? false);
  }

  get_browser_advice() {
    return this.browser_info.get_advice(this.webgpu_supported ?? false);
  }

  footer(text: string, index: number) {
    const el = document.getElementById('app-footer') as HTMLDivElement | null;
    if (el && index < this.footerstrings.length) {
      this.footerstrings[index] = text;
      let msg = "";
      for (const s of this.footerstrings) {
        if (s.length > 0) {
          if (msg.length > 0) msg += " - ";
          msg += s;
        }
      }
      el.textContent = msg;
    }
  }


  private frame = (time: number) => {
    const delta_time =
      this.lastFrameTime === null ? 0 : (time - this.lastFrameTime) / 1000.0;

    this.lastFrameTime = time;
    
    this.scene_service.tick(delta_time);
    this.input_service.end_frame();
    this.input_service.begin_frame();

    const device = this.shader_service.get_device();
    if (device)
    {
       this.scene_service.prepare(device);
    }
    this.render_service.render(time);

    this.animationFrameId = requestAnimationFrame(this.frame);
  };


  static instance(): Stage {
    const g = globalThis as GlobalStage;
    if (!g.__stage) {
      g.__stage = new Stage();
    }
    return g.__stage;
  }

}
